/* ============================================================
   ET - COLLECTION SEO TEXT — measure, decide, animate the clamp

   The stylesheet clamps the rich-text flow to a fixed number of
   body lines and clips the rest. This script decides whether the
   toggle is needed at all, and animates the box between the two
   heights.

   WHY NOT scrollHeight. The obvious test — scrollHeight >
   clientHeight — silently returns false here, on desktop, every
   time. The clamped box is a MULTI-COLUMN container: its overflow
   goes in the INLINE direction (overflow columns are placed past
   the last column), so scrollHeight never exceeds clientHeight and
   only scrollWidth would grow. And the stylesheet uses
   `overflow: clip`, which leaves no scrollable overflow region at
   all, so neither dimension reports anything beyond the padding
   box. Either way the toggle would never appear. The box is
   therefore measured explicitly instead, once with the clamp
   lifted and once with it forced on.

   No matchMedia anywhere: the 990px breakpoint lives entirely in
   the stylesheet (it swaps --et-seo-lines and --et-seo-body-lh)
   and this script only ever reads the rendered box, so the JS and
   CSS breakpoints cannot drift apart.

   `height: auto` IS ANIMATABLE HERE. Impact sets
   `interpolate-size: allow-keywords` on <html> (theme.css:12), so
   every hand-back to a stylesheet `auto` is a real 320ms
   transition in Chrome and Edge, not the no-op it would be on a
   stock page. Every point where this file returns the box to the
   stylesheet therefore goes through settle(), which commits the
   new resting height with transitions pinned off. Skipping it
   makes the block animate itself shut and back open after a
   measurement.

   This file also takes over the pre-paint clamp: the inline script
   under the section markup applies .et-seo--preclamp during
   parsing (this file is deferred, so it cannot), and init() drops
   it the moment the real clamp goes on.
   ============================================================ */

(function () {
  'use strict';

  /* The section emits one script tag per instance; only the first should bind. */
  if (window.etCollectionSeoTextLoaded) return;
  window.etCollectionSeoTextLoaded = true;

  var ROOT_SELECTOR = '[data-et-seo]';
  var DURATION = 320; /* keep in step with the height transition in the stylesheet */
  var EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
  var RESIZE_DEBOUNCE = 150;

  function qsa(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clearPending(state) {
    if (state.endHandler) {
      state.rte.removeEventListener('transitionend', state.endHandler);
      state.endHandler = null;
    }
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }

  /* ------------------------------------------------------------------ *
   * Measurement
   * ------------------------------------------------------------------ */

  /* Both measurement classes are added, read and removed inside this one task. A layout read
     between the add and the remove forces a synchronous reflow, so the browser never paints the
     intermediate box — and the classes carry `transition: none` so the measurement itself cannot
     kick off an animation. */
  function measure(state) {
    var root = state.root;
    var rte = state.rte;

    root.classList.add('is-measuring');
    var natural = Math.ceil(rte.getBoundingClientRect().height);

    /* Two desktop columns only read well while the whole block fits roughly on one screen. On the
       store's long-copy collections (washok-kasten is 15,104 characters) the open state balances
       into two columns several viewport-heights tall, so the reader has to travel all the way down
       column 1 and then back up to the top of column 2 — the section works but cannot be read in
       order. Past ~1.2 viewport heights the OPEN state drops to a single column; the collapsed
       state keeps the Figma's two.

       The class changes the natural height (one column is twice as tall as two), so it is applied
       while the clamp is still lifted and the height re-read — otherwise the open animation would
       target a height the expanded box never has. It cannot oscillate: dropping to one column only
       ever makes the block taller, so a block over the threshold stays over it. */
    var single = natural > Math.round(window.innerHeight * 1.2);
    if (single !== root.classList.contains('et-seo--single-column')) {
      root.classList.toggle('et-seo--single-column', single);
      natural = Math.ceil(rte.getBoundingClientRect().height);
    }

    root.classList.remove('is-measuring');

    root.classList.add('is-measuring-clamp');
    var clamped = Math.ceil(rte.getBoundingClientRect().height);
    root.classList.remove('is-measuring-clamp');

    state.natural = natural;
    state.clamped = clamped;

    /* One pixel of tolerance: sub-pixel line boxes round the two reads apart on their own, and a
       toggle that opens the block by half a line is worse than no toggle. */
    var overflows = natural > clamped + 1;

    root.classList.toggle('has-toggle', overflows);
    root.classList.toggle('et-seo--fits', !overflows);

    /* A width change can turn a five-line paragraph into four. If the text now fits, the open
       state has nothing left to mean. */
    if (!overflows && state.expanded) setExpanded(state, false, false);

    settle(state);

    return overflows;
  }

  /* Commit whatever height the measurement decided on WITHOUT letting it animate.

     The last thing `is-measuring-clamp` committed is the clamp height, and it carried
     `transition: none`. Removing it and settling on `height: auto` (et-seo--fits, or is-expanded)
     is a plain height change with the stylesheet's 320ms transition live again — and theme.css
     puts `interpolate-size: allow-keywords` on <html>, so `auto` really is interpolable and the
     transition really runs. Without this flush the "text fits" case paints at the clamp and then
     animates down to its natural height, shifting the whole page up after it had settled; and an
     open block snaps shut to the clamp and re-opens on every debounced resize step. Pinning
     `transition: none` across one forced layout makes the new resting height the before-change
     style with nothing to transition, and handing the property back afterwards changes no height,
     so nothing animates either way. */
  function settle(state) {
    var rte = state.rte;
    rte.style.transition = 'none';
    void rte.offsetHeight;
    rte.style.transition = '';
  }

  function remeasure(state) {
    if (!state.root.__etSeoInit) return;

    /* Hand the box back to the stylesheet first: an inline height left over from an interrupted
       animation would otherwise be what the natural read reports. */
    clearPending(state);
    state.rte.style.transition = '';
    state.rte.style.height = '';

    measure(state);
  }

  /* ------------------------------------------------------------------ *
   * Open / close
   * ------------------------------------------------------------------ */

  function setExpanded(state, expanded, useAnimation) {
    var rte = state.rte;

    clearPending(state);

    /* Read the current height BEFORE the class flip, or the forced layout would report the
       height the new state asks for rather than the one being animated away from. */
    var start = rte.getBoundingClientRect().height;

    state.expanded = expanded;
    state.root.classList.toggle('is-expanded', expanded);
    if (state.toggle) state.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (!useAnimation || prefersReducedMotion()) {
      rte.style.height = '';
      /* Same reason as after a measurement: `auto` is interpolable here, so simply handing the box
         back to the stylesheet would animate the very transition this branch exists to skip — and
         the focus rescue in particular must not leave the browser scrolling towards a box that is
         still growing. */
      settle(state);
      return;
    }

    /* Expanded rests at `height: auto`, which has nothing to interpolate to, so the animation
       runs between two pixel values and the resting state is handed back at the end. */
    var target = expanded ? state.natural : state.clamped;

    rte.style.transition = 'none';
    rte.style.height = start + 'px';
    void rte.offsetHeight; /* flush, so the next height change transitions instead of jumping */
    rte.style.transition = 'height ' + DURATION + 'ms ' + EASE;
    rte.style.height = target + 'px';

    var done = function (event) {
      if (event && event.target !== rte) return;
      clearPending(state);
      /* Back to the stylesheet rather than an inline pixel value, so an interrupted transition
         still lands on a correct resting height — settled, so the last sub-pixel between the
         animation's end value and the resting `auto` cannot re-arm a fresh 320ms transition. */
      rte.style.height = '';
      settle(state);
    };

    state.endHandler = done;
    rte.addEventListener('transitionend', done);
    /* transitionend is skipped when the tab is backgrounded mid-animation. */
    state.timer = setTimeout(done, DURATION + 80);
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  function schedule(state) {
    if (state.debounce) clearTimeout(state.debounce);
    state.debounce = setTimeout(function () {
      state.debounce = null;
      remeasure(state);
    }, RESIZE_DEBOUNCE);
  }

  function init(root) {
    if (root.__etSeoInit) return;

    var rte = root.querySelector('[data-et-seo-rte]');
    if (!rte) return;

    var state = {
      root: root,
      rte: rte,
      toggle: root.querySelector('[data-et-seo-toggle]'),
      container: root.querySelector('.et-collection-seo-text__container') || root,
      expanded: false,
      natural: 0,
      clamped: 0,
      width: 0,
      endHandler: null,
      timer: null,
      debounce: null,
      observer: null,
      onResize: null
    };

    root.__etSeoInit = true;
    root.__etSeoState = state;

    /* The clamp is applied HERE, not in the resting stylesheet: if this file never runs the
       visitor gets the whole text and no button, rather than text clipped with no way to open it.
       The inline script under the section markup holds a `max-height` stand-in until this point so
       the un-clamped flow never paints; it goes now, because the open animation and the expanded
       state both need a real height that a leftover max-height would clip. */
    root.classList.remove('et-seo--preclamp');
    root.classList.add('et-seo--js');
    measure(state);

    if (state.toggle) {
      state.toggle.addEventListener('click', function () {
        setExpanded(state, !state.expanded, true);
      });
    }

    /* A link inside a clipped overflow column is still focusable, so tabbing into the collapsed
       block would otherwise move focus somewhere invisible. Opened without the animation on
       purpose — the browser scrolls to the focused element immediately, and it should not be
       chasing a box that is still growing. */
    rte.addEventListener('focusin', function () {
      if (!state.expanded && root.classList.contains('has-toggle')) setExpanded(state, true, false);
    });

    /* The clamp is a fixed pixel height, but the NATURAL height depends on how the text wraps,
       which depends on the width and on which face is actually loaded. */
    if (window.ResizeObserver) {
      state.width = Math.round(state.container.getBoundingClientRect().width);
      state.observer = new ResizeObserver(function (entries) {
        var width = Math.round(entries[0].contentRect.width);
        /* The container also grows in HEIGHT while the block opens — re-measuring on that would
           feed this observer its own animation. Width is the only thing that matters. */
        if (width === state.width) return;
        state.width = width;
        schedule(state);
      });
      state.observer.observe(state.container);
    } else {
      state.onResize = function () {
        schedule(state);
      };
      window.addEventListener('resize', state.onResize);
    }

    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(function () {
        remeasure(state);
      });
    }
  }

  function boot(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) init(scope);
    qsa(scope, ROOT_SELECTOR).forEach(init);
  }

  function teardown(scope) {
    if (!scope) return;

    qsa(scope, ROOT_SELECTOR).forEach(function (root) {
      var state = root.__etSeoState;
      if (!state) return;

      clearPending(state);
      if (state.debounce) clearTimeout(state.debounce);
      if (state.observer) state.observer.disconnect();
      if (state.onResize) window.removeEventListener('resize', state.onResize);

      root.__etSeoState = null;
      root.__etSeoInit = false;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });
})();
